import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { RouteAssignment } from '../../../../shared/models/route-assignment';
import { DatePipe } from '@angular/common';
import { ConfigurationModel, FeatureFlagEnum } from '../../../../shared/models/configuration.model';
import moment from 'moment';
import { ImageModel } from '../../../../shared/models/image';
import { Observation } from '../../../../shared/models/observation';
import { ShiftsService } from '../../../../data/shifts/shifts.service';
import { ShiftDistance, ShiftWithDriverAndVehicleModel } from '../../../../shared/models/shift.model';
import { ShiftMapDataService } from '../../services/shift-map-data.service';
import { ActionMenuItem } from '../../../../shared/models/action-menu-item.class';
import { HighlightedItemSource } from '../../../../shared/models/highlighted-item-update';
import { HardwareConfiguration, SensorType } from 'src/app/shared/models/vehicle.model';
import { MapStyles } from '../../../../configuration/map-styles';
import { ToastService } from '../../../../shared/services/toast.service';
import { ActionMenuItemComponent } from '../../../../shared/components/action-menu-item/action-menu-item.component';
import { DurationPipe } from '../../../../shared/formatting/duration.pipe';
import { DistancePipe } from '../../../../shared/formatting/distance.pipe';
import { LocationApiService } from '../../../../data/location-api/location-api.service';

@Component({
  selector: 'app-assignment-map-panel',
  imports: [DatePipe, ActionMenuItemComponent, DurationPipe, DistancePipe],
  providers: [DatePipe],
  templateUrl: './assignment-map-panel.component.html',
  styleUrl: './assignment-map-panel.component.scss',
})
export class AssignmentMapPanelComponent implements OnInit, OnChanges {
  @Input() shift: ShiftWithDriverAndVehicleModel;
  @Input() assignment: RouteAssignment;
  @Input() configuration: ConfigurationModel;
  @Input() images: ImageModel[];
  @Input() observations: Observation[];

  distances: ShiftDistance;
  highlightedObservationId: number;
  observationItems: ActionMenuItem[];

  FeatureFlagEnum = FeatureFlagEnum;
  HardwareConfiguration = HardwareConfiguration;
  SensorType = SensorType;

  constructor(
    private shiftService: ShiftsService,
    private shiftMapManager: ShiftMapDataService,
    private locationApi: LocationApiService,
    private datePipe: DatePipe,
    private toast: ToastService
  ) {}

  ngOnInit() {
    this.shiftService
      .getShiftDistanceDriven(this.assignment.shiftId, this.assignment.id)
      .then(response => {
        this.distances = response.data;
      })
      .catch(error => {
        console.error(error);
        this.distances = null;
        this.toast.short('Error getting track lengths.');
      });

    this.shiftMapManager.highlightedObservation$.subscribe(highlightedObservationUpdate => {
      this.highlightedObservationId = highlightedObservationUpdate.itemId;
      if (!!this.observations) {
        this.updateObservationItems();
      }
    });

    if (!this.shift.end && !this.assignment.completed) {
      // live shift and the assignment not completed yet
      this.locationApi.getRouteAssignmentTracking(this.assignment.configId, this.assignment.routeId).then(response => {
        const status = response.find(status => status.sessionkey === this.shift.id);
        if (status) {
          this.assignment.percentCompleted = status.percent_complete ?? 0;
          this.assignment.distanceDrivenOffRoute = status.distance_off_route_meters;
          this.assignment.distanceDrivenOnRoute = status.distance_on_route_meters;
          this.assignment.timeDrivenOffRoute = status.time_off_route_seconds;
          this.assignment.timeDrivenOnRoute = status.time_on_route_seconds;
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const observations = changes['observations']?.currentValue as Observation[];
    if (!!observations) {
      this.updateObservationItems();
    }
  }

  getDuration() {
    const end = moment(this.assignment.completed);
    const start = moment(this.assignment.onAssignmentFrom);
    return moment.duration(end.diff(start));
  }

  hasFeatureFlag(featureFlag: string): boolean {
    return this.configuration.featureFlags.find(value => value.isEnabled && value.name === featureFlag) !== undefined;
  }

  private updateObservationItems() {
    this.observationItems = this.observations.map(observation => this.observationToActionMenuItem(observation));
  }

  private observationToActionMenuItem(observation: Observation): ActionMenuItem {
    return new ActionMenuItem(
      observation.id,
      'stars',
      observation.observationType.title,
      this.datePipe.transform(observation.created, 'shortTime'),
      '',
      null,
      !!observation.observationType.group?.mapColor ? observation.observationType.group.mapColor : MapStyles.LIVE_COLOR,
      () => this.highlightedObservationId === observation.id,
      null,
      null,
      () => this.selectObservation(observation),
      null,
      null,
      []
    );
  }

  private selectObservation(observation: Observation) {
    this.shiftMapManager.sendHighlightedObservationUpdate(observation.id, HighlightedItemSource.PANEL);
  }
}
